import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';
import { doc, getDoc, collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { ref, onValue } from "firebase/database";
import { httpsCallable } from 'firebase/functions';

import { useAppContext } from '../AppContext';
import UniversalMediaRenderer from './UniversalMediaRenderer';
import SealedWhisperCard from './SealedWhisperCard';
import MarkdownRenderer from './MarkdownRenderer';
import HoverTooltip from './HoverTooltip';
import AIGeneratedContentModal from './AIGeneratedContentModal';
import { ReportModal } from './ReportModal';
import GiftModal from './GiftModal';
import AmplifyModal from './AmplifyModal';
import { getSentimentGradient } from '../utils/sentimentUtils';
import BadgeIconStrip from './BadgeIconStrip';
import withVisionAura from './visions/withVisionAura';

// Enhanced Profile Picture - "Floating Orb" style
const ProfilePicture = withVisionAura(({ src, alt, onClick, className }) => (
    <div className="relative group cursor-pointer" onClick={onClick}>
        <div className="absolute inset-0 rounded-full bg-white/20 blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
        <img 
            src={src} 
            alt={alt} 
            className={`${className} relative z-10 border border-white/20 shadow-lg`} 
        />
    </div>
));

const CardAmplifiers = ({ amplifiers, getUserDisplayName, onUserSelect, t }) => {
    const [show, setShow] = useState(false);
    if (!amplifiers || amplifiers.length === 0) return null;

    return (
        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between group/amp relative z-20">
            <button 
                onClick={() => setShow(!show)} 
                className="flex items-center text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-all"
            >
                <LucideIcons.Flame size={14} className="mr-2 text-amber-500 group-hover/amp:text-amber-600 transition-colors" />
                {t('postCard.amplifiedBy', { count: amplifiers.length })}
            </button>
            
            {show && (
                <div className="flex flex-wrap gap-2 justify-end max-w-[70%] animate-in fade-in slide-in-from-right-4 duration-300">
                    {amplifiers.slice(0, 5).map(([id, amount]) => (
                        <span key={id} className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1 rounded-full border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-all" onClick={() => onUserSelect(id)}>
                            {getUserDisplayName(id)}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

const CardHeader = ({ post, onUserSelect, context, isAuthorPresent, isPro, authorBadges, onShareToVision, currentUserId, t }) => {
    const { appFunctions, setMessage, showConfirmation, userConnections, setUserConnections } = useAppContext();
    const isRevealed = post.revealedTo && post.revealedTo.includes(currentUserId) && post.echoesInvested >= 30;
    const isInnerCircle = userConnections?.some(c => c.followingId === post.authorId && c.tier === 'Inner Circle');

    const handleToggleInnerCircle = (e) => {
        e.stopPropagation();
        const funcName = isInnerCircle ? 'removeInnerCircleMember' : 'addInnerCircleMember';
        
        showConfirmation({
            message: isInnerCircle ? "Remove from Inner Circle?" : "Add to Inner Circle?",
            onConfirm: async () => {
                try {
                    const func = httpsCallable(appFunctions, funcName);
                    await func({ targetUserId: post.authorId });
                     if (typeof setUserConnections === 'function') {
                        setUserConnections(prev => {
                             const exists = prev.some(c => c.followingId === post.authorId);
                             if (exists) {
                                 return prev.map(c => {
                                     if (c.followingId === post.authorId) {
                                         return { ...c, tier: isInnerCircle ? 'Following' : 'Inner Circle' };
                                     }
                                     return c;
                                 });
                             } else if (!isInnerCircle) {
                                  return [...prev, { followingId: post.authorId, tier: 'Inner Circle' }];
                             }
                             return prev;
                        });
                    }
                    setMessage(isInnerCircle ? "Removed" : "Added");
                } catch (error) {
                    setMessage(error.message);
                }
            }
        });
    };

    if (!isRevealed && context !== 'nexus') {
        return (
            <div className="flex justify-between items-center mb-4 opacity-70 hover:opacity-100 transition-opacity">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shadow-inner">
                        <LucideIcons.CloudFog size={18} className="text-gray-400" />
                    </div>
                    <div>
                        <p className="font-medium text-gray-400 text-sm">Mysterious Whisper</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex justify-between items-start mb-4 relative z-20">
            <div className="flex items-center gap-3">
                <div className="relative">
                     <ProfilePicture 
                        src={post.authorPhotoURL || "https://placehold.co/40x40/AEC6CF/FFFFFF?text=U"} 
                        alt={post.authorName} 
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border border-white/10 shadow-lg" 
                        onClick={() => onUserSelect(post.authorId)} 
                    />
                     {isAuthorPresent && <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 border-2 border-[#030308] rounded-full shadow-[0_0_8px_rgba(74,222,128,0.8)]"></span>}
                </div>
                <div className="flex flex-col">
                    <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-white text-base cursor-pointer hover:text-purple-300 transition-colors" onClick={() => onUserSelect(post.authorId)}>
                            {post.authorName}
                        </span>
                        {isPro && <LucideIcons.Crown size={14} className="text-amber-400 drop-shadow-[0_0_5px_rgba(251,191,36,0.8)]" />}
                        <BadgeIconStrip badges={authorBadges} size="xs" />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-400 font-medium">
                        <span>{post.timestamp?.toDate ? new Date(post.timestamp.toDate()).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Now'}</span>
                        {currentUserId !== post.authorId && (
                            <>
                                <span className="w-1 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></span>
                                <button 
                                    onClick={handleToggleInnerCircle} 
                                    className={`transition-colors font-semibold ${isInnerCircle ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
                                >
                                    {isInnerCircle ? "Inner Circle" : "Follow"}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
            
            <button onClick={() => onShareToVision(post)} className="text-gray-400 hover:text-white transition-all p-2 rounded-full hover:bg-white/10">
                <LucideIcons.Share2 size={20} strokeWidth={2} />
            </button>
        </div>
    );
};

const CardBody = ({ entry, echoedWhisper, getUserDisplayName, content, isLoadingContent, t }) => {
    return (
        <div className="space-y-4 relative z-20">
            {echoedWhisper && (
                <div className="relative pl-4 py-2 overflow-hidden group/echo border-l-2 border-purple-500 bg-white/5 rounded-r-lg">
                    <p className="text-xs font-semibold text-purple-400 mb-1 flex items-center gap-1.5">
                        <LucideIcons.Repeat size={14} />
                        Echoed from {getUserDisplayName(echoedWhisper.authorId)}
                    </p>
                    <p className="text-sm text-gray-300 italic">"{echoedWhisper.content}"</p>
                </div>
            )}
            
            {/* Media */}
            <div className="relative rounded-xl overflow-hidden bg-black/40 border border-white/5 mb-4 shadow-inner">
                <UniversalMediaRenderer entry={entry} />
            </div>

            {/* Content Text */}
            {isLoadingContent ? (
                <div className="space-y-2 animate-pulse opacity-50">
                    <div className="h-2.5 bg-white/10 rounded-full w-full"></div>
                    <div className="h-2.5 bg-white/10 rounded-full w-3/4"></div>
                </div>
            ) : content ? (
                <div className="prose prose-invert prose-p:text-gray-200 prose-p:text-base prose-p:leading-relaxed max-w-none">
                    <MarkdownRenderer>{content}</MarkdownRenderer>
                </div>
            ) : null}

            {/* Tags */}
            {(entry.tags?.length > 0 || entry.aiTags?.length > 0) && (
                <div className="flex flex-wrap gap-2 pt-2">
                    {entry.tags?.map(tag => (
                        <span key={tag} className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 font-medium hover:bg-gray-200 dark:hover:bg-gray-700 cursor-pointer transition-colors">#{tag}</span>
                    ))}
                    {entry.aiTags?.map(tag => (
                        <span key={tag} className="text-xs px-2.5 py-1 rounded-md bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 font-medium cursor-pointer transition-colors flex items-center gap-1">
                            <LucideIcons.Sparkles size={12} /> {tag}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

function PostCard({ post: initialPost, context = 'feed', index, userId, getUserDisplayName, onUserSelect, onShowComments, className, onShareToVision, contentType }) {
    const { t } = useTranslation();
    const { db, rtdb, doc: fbDoc, getDoc: fbGetDoc, userProfiles, appId } = useAppContext();
    const [post, setPost] = useState(initialPost);
    const [authorProfile, setAuthorProfile] = useState(null);
    const [content, setContent] = useState(post.content);
    const [isLoadingContent, setIsLoadingContent] = useState(false);
    const [isAuthorPresent, setIsAuthorPresent] = useState(false);

    const [aiModalContent, setAiModalContent] = useState(null);
    const [showReportModal, setShowReportModal] = useState(false);
    const [showGiftModal, setShowGiftModal] = useState(false);
    const [showAmplifyModal, setShowAmplifyModal] = useState(false);
    const [echoedWhisper, setEchoedWhisper] = useState(null);

    useEffect(() => {
        const postRef = fbDoc(db, `artifacts/${appId}/public/data/whispers`, initialPost.id);
        const unsubscribe = onSnapshot(postRef, async (doc) => {
            if (doc.exists()) {
                const postData = { id: doc.id, ...doc.data() };
                setPost(postData);
                const isRevealed = postData.revealedTo?.includes(userId) && postData.echoesInvested >= 30;
                if (isRevealed && !authorProfile) {
                    const pRef = fbDoc(db, `artifacts/${appId}/public/data/user_profiles`, postData.authorId);
                    const snap = await fbGetDoc(pRef);
                    if (snap.exists()) setAuthorProfile(snap.data());
                }
            }
        });
        return () => unsubscribe();
    }, [db, initialPost.id, userId, authorProfile, appId]);

    useEffect(() => {
        if (!authorProfile && post.authorId) {
            const p = userProfiles.find(x => x.id === post.authorId);
            if (p) setAuthorProfile(p);
        }
    }, [userProfiles, post.authorId, authorProfile]);

    useEffect(() => {
        if (post.content === undefined) {
             setIsLoadingContent(true);
             fbGetDoc(fbDoc(db, `artifacts/${appId}/public/data/whispers/${post.id}/private/content`))
                .then(s => { if(s.exists()) setContent(s.data().content); else setContent(''); })
                .catch(() => setContent(''))
                .finally(() => setIsLoadingContent(false));
        } else {
            setContent(post.content);
        }
    }, [post.id, post.content]);

    useEffect(() => {
        if (post.isEcho && post.echoedWhisperId) {
             fbGetDoc(fbDoc(db, `artifacts/${appId}/public/data/whispers`, post.echoedWhisperId))
                .then(s => { if(s.exists()) setEchoedWhisper({id:s.id, ...s.data()}); });
        }
    }, [post.isEcho, post.echoedWhisperId]);

    useEffect(() => {
        if (!rtdb || !post.authorId) return;
        return onValue(ref(rtdb, `whisper_presence/${post.id}/${post.authorId}`), s => setIsAuthorPresent(s.exists()));
    }, [rtdb, post.id, post.authorId]);

    const isSuperAmplified = useMemo(() => post.superAmplifiedUntil && post.superAmplifiedUntil.toDate() > new Date(), [post.superAmplifiedUntil]);

    // WHISPER CONTAINER STYLING
    // -------------------------
    const cardBaseStyle = "relative p-4 sm:p-6 rounded-2xl mb-4 sm:mb-6 glass-panel glass-panel-hover group";
    
    // Legacy support for user themes
    const materialStyle = authorProfile?.profileTheme 
        ? `comment-card-theme-${authorProfile.profileTheme}` 
        : "";

    const amplifiedStyle = isSuperAmplified 
        ? "ring-2 ring-amber-400 shadow-[0_0_15px_rgba(251,191,36,0.5)]" 
        : "";
    
    let finalClass = `${cardBaseStyle} ${materialStyle} ${amplifiedStyle} ${className || ''}`;

    const headerPost = useMemo(() => ({ ...post, authorName: authorProfile?.displayName || post.authorName, authorPhotoURL: authorProfile?.photoURL || post.authorPhotoURL }), [post, authorProfile]);

    if (post.isSealed) return <SealedWhisperCard entry={post} getUserDisplayName={getUserDisplayName} />;

    return (
        <>
            {aiModalContent && <AIGeneratedContentModal title="Analysis" content={aiModalContent} onClose={() => setAiModalContent(null)} />}
            {showReportModal && <ReportModal contentId={post.id} contentType="whisper" authorId={post.authorId} onClose={() => setShowReportModal(false)} />}
            {showGiftModal && authorProfile && <GiftModal author={authorProfile} whisper={post} onClose={() => setShowGiftModal(false)} />}
            {showAmplifyModal && <AmplifyModal whisper={post} onClose={() => setShowAmplifyModal(false)} />}

            <div className={finalClass} style={{ animationDelay: `${index * 150}ms` }}>


                {/* 3. Main Content Layer */}
                <div className="relative z-10">
                    <CardHeader 
                        post={headerPost} 
                        onUserSelect={onUserSelect} 
                        context={context} 
                        isAuthorPresent={isAuthorPresent} 
                        isPro={authorProfile?.proStatus === 'active'} 
                        authorBadges={authorProfile?.badges} 
                        onShareToVision={onShareToVision} 
                        currentUserId={userId} 
                        t={t} 
                    />
                    
                    <CardBody 
                        entry={post} 
                        echoedWhisper={echoedWhisper} 
                        getUserDisplayName={getUserDisplayName} 
                        content={content} 
                        isLoadingContent={isLoadingContent} 
                        t={t} 
                    />

                    <CardAmplifiers 
                        amplifiers={Object.entries(post.amplifiers || {})} 
                        getUserDisplayName={getUserDisplayName} 
                        onUserSelect={onUserSelect} 
                        t={t} 
                    />
                </div>
            </div>
        </>
    );
}

export default PostCard;